// src/adm/pages/Branch.jsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdmNav from "../components/AdmNav";
import ToggleSwitch from "../components/ToggleSwitch";
import { AdmBranchAPI } from "../api";


// 기존 adm의 area_sort 옵션과 표시 라벨 매핑 (1~10)
const AREA_OPTIONS = [
  { value: "1", label: "서울권" },
  { value: "2", label: "경기권" },
  { value: "3", label: "인천권" },
  { value: "4", label: "경상권" },
  { value: "5", label: "충청권" },
  { value: "6", label: "전라권" },
  { value: "7", label: "강원권" },
  { value: "8", label: "제주권" },
  { value: "9", label: "CHINA" },
  { value: "10", label: "OTHER" },
];

// 드롭다운: 항상 전체 지점 옵션 렌더링
function BranchSelect({ options, value, onChange }) {
  const groupedOptions = useMemo(() => {
    const list = [];
    let currentArea = "";
    (options || []).forEach((b) => {
      if (b.branch_area !== currentArea) {
        currentArea = b.branch_area;
        list.push({ type: "header", text: `-----${currentArea}-----` });
      }
      list.push({ type: "item", value: String(b.branch_idx), text: b.branch_name });
    });
    return list;
  }, [options]);

  return (
    <select
      id="branch"
      className=""
      value={value}
      onChange={(e) => onChange(e.target.value)} // 선택 즉시 필터 트리거
    >
      <option value="">지점선택</option>
      {groupedOptions.map((opt, i) =>
        opt.type === "header" ? (
          <option key={`h-${i}`} value="" disabled>
            {opt.text}
          </option>
        ) : (
          <option key={opt.value} value={opt.value}>
            {opt.text}
          </option>
        )
      )}
    </select>
  );
}

// 지점 추가 모달 — 기존 branch.html과 같은 필드/라벨/초기값
function AddBranchModal({ open, onClose, onSubmit }) {
  const [name, setName] = useState("");
  const [eng, setEng] = useState("");
  const [areaSort, setAreaSort] = useState("-1");
  const [active, setActive] = useState(false);
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) {
      setName("");
      setEng("");
      setAreaSort("-1");
      setActive(false);
      setPending(false);
      setErr("");
    }
  }, [open]);

  if (!open) return null;
  const areaLabel = AREA_OPTIONS.find((o) => o.value === areaSort)?.label || "";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setErr("지점명을 입력하세요.");
      return;
    }
    if (areaSort === "-1") {
      setErr("지역을 선택하세요.");
      return;
    }

    try {
      setPending(true);
      setErr("");
      await onSubmit({
        branch_name: name.trim(),
        branch_eng: eng.trim(),
        branch_area: areaLabel, // 텍스트(서울권 등)
        area_sort: areaSort, // 숫자코드(1~10)
        status: active ? 1 : 9, // 기존과 동일(1/9)
      });
    } catch (e2) {
      setErr(e2?.message || "등록 중 오류");
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="adm-modal-backdrop" onClick={onClose}>
      <div className="adm-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="adm-modal-title">지점 추가</h3>

        <form onSubmit={handleSubmit} className="adm-modal-form">
          <label className="adm-field">
            <span className="adm-label">지점명</span>
            <input placeholder="지점명을 입력하세요" value={name} onChange={(e) => setName(e.target.value)} />
          </label>

          <label className="adm-field">
            <span className="adm-label">지점 영문</span>
            <input placeholder="지점명을 입력하세요" value={eng} onChange={(e) => setEng(e.target.value)} />
          </label>

          <label className="adm-field">
            <span className="adm-label">지역</span>
            <select value={areaSort} onChange={(e) => setAreaSort(e.target.value)}>
              <option value="-1">지역을 선택하세요</option>
              {AREA_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>

          <label className="adm-field">
            <span className="adm-label">상태</span>
            <ToggleSwitch checked={active} onChange={setActive} />
          </label>

          {err && <p className="adm-error">{err}</p>}

          <div className="adm-modal-actions">
            <button type="button" className="btn cancel" onClick={onClose} disabled={pending}>
              취소
            </button>
            <button type="submit" className="btn primary" disabled={pending}>
              {pending ? "추가 중..." : "추가"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function BranchPage() {
  const nav = useNavigate();

  const [allList, setAllList] = useState([]); 
  const [list, setList] = useState([]); 

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [selectedBranch, setSelectedBranch] = useState(""); // dropdown 값
  const [openAdd, setOpenAdd] = useState(false);

  const fetchAndRender = async (branch_idx) => {
    try {
      setLoading(true);
      setErr("");
      const rows = await AdmBranchAPI.list({ branch_idx });
      setList(Array.isArray(rows) ? rows : []);
    } catch (e) {
      setErr(e?.message || "로드 실패");
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setErr("");
        const rows = await AdmBranchAPI.list({});
        const safe = Array.isArray(rows) ? rows : [];
        setAllList(safe);
        setList(safe); 
      } catch (e) {
        setErr(e?.message || "로드 실패");
        setAllList([]);
        setList([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSelect = async (val) => {
    setSelectedBranch(val);
    await fetchAndRender(val || undefined);
  };

  const onSearch = () => {
    fetchAndRender(selectedBranch || undefined);
  };

  const onToggle = async (row, next) => {
    const prev = list.slice();
    try {
      setList((old) => old.map((it) => (it.branch_idx === row.branch_idx ? { ...it, status: next ? "1" : "9" } : it)));
      await AdmBranchAPI.toggleStatus({
        branch_idx: row.branch_idx,
        status: next ? "1" : "9",
      });
    } catch (e) {
      setList(prev); 
      alert(e?.message || "상태 변경 실패");
    }
  };

  const goUser = (branch_name) => {
    nav(`/adm/user?branch_name=${encodeURIComponent(branch_name)}`);
  };

  const onCreate = async (payload) => {
    await AdmBranchAPI.create(payload);
    setOpenAdd(false);
    await fetchAndRender(selectedBranch || undefined);
    try {
      const rows = await AdmBranchAPI.list({});
      setAllList(Array.isArray(rows) ? rows : []);
    } catch {}
  };

  return (
    <>
      <AdmNav />
      <section className="adm-branch">
        <div className="adm-card">
          <div className="adm-card-head">
            <div className="adm-title-row">
              <h2>지점 리스트</h2>
              <button className="btn primary" onClick={() => setOpenAdd(true)}>
                + 지점 추가
              </button>
            </div>

            <div className="adm-toolbar">
              <BranchSelect options={allList} value={selectedBranch} onChange={handleSelect} />
              <button className="btn" onClick={onSearch}>
                검색
              </button>
            </div>
          </div>

          <div className="adm-table-wrap">
            <table className="adm-table" cellPadding={0} cellSpacing={0}>
              <thead>
                <tr className="table-header">
                  <th>지점명</th>
                  <th>지점 영문</th>
                  <th>지역</th>
                  <th>API 사용량</th>
                  <th>상태</th>
                  <th>회원 보기</th>
                </tr>
              </thead>
              <tbody id="branchTableBody">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="center">
                      로딩 중…
                    </td>
                  </tr>
                ) : err ? (
                  <tr>
                    <td colSpan={6} className="center error">
                      {err}
                    </td>
                  </tr>
                ) : list.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="center">
                      결과가 없습니다.
                    </td>
                  </tr>
                ) : (
                  list.map((c) => (
                    <tr key={c.branch_idx} className="table-row border-b">
                      <td className="p-3">{c.branch_name}</td>
                      <td className="p-3">{c.branch_eng}</td>
                      <td className="p-3">{c.branch_area}</td>
                      <td className="p-3">{c.total_api_calls}</td>
                      <td className="p-3">
                        <ToggleSwitch checked={String(c.status) === "1"} onChange={(next) => onToggle(c, next)} />
                      </td>
                      <td className="p-3">
                        <button className="btn small" onClick={() => goUser(c.branch_name)}>
                          회원 보기
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <AddBranchModal open={openAdd} onClose={() => setOpenAdd(false)} onSubmit={onCreate} />
      </section>
    </>
  );
}
